import { safeError } from "../../lib/apiError";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isConsentTryAgain, type ConsentTopic, type TopicKey } from "../../api/consent";
import logger from "../../lib/logger";
import ConsentChecklist, { openConsentPage } from "./ConsentChecklist";
import ConsentDialog, {
  DialogError,
  DialogText,
  DialogTitle,
  LINK_BUTTON,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from "./ConsentDialog";

function GroupTitle({ children }: { children: React.ReactNode }) {
  return <div className="mt-4 mb-2 text-sm font-semibold text-[var(--foreground)]">{children}</div>;
}

/** "I accept the [Terms of use]." — the link sits inside the sentence, so the sentence comes in three pieces. */
function LinkedLine({
  before,
  link,
  after,
  onLink,
  testId,
}: {
  before: string;
  link: string;
  after: string;
  onLink: () => void;
  testId: string;
}) {
  return (
    <>
      {before}
      <button type="button" data-testid={testId} className={`${LINK_BUTTON} font-normal`} onClick={onLink}>
        {link}
      </button>
      {after}
    </>
  );
}

/**
 * The shared body of the gate dialogs (Before you start, We updated our terms): Required (the terms box, the
 * privacy-notice line) and the optional / changed device rows, all in one list confirmed in one tap.
 * - Optional rows present: Decline · Accept selected (enabled once the terms box is ticked) · Accept all.
 * - No optional rows: Decline · Continue (enabled once the terms box is ticked).
 * `onConfirm` gets the ticked keys and sends one batch; a throw shows "try again" (503 / no network) or an error.
 */
export default function ConsentGateList({
  testId,
  title,
  text,
  intro,
  required,
  optional,
  optionalTitle,
  termsKeys,
  declineLabel,
  onConfirm,
  onDecline,
}: {
  testId: string;
  title: string;
  text: string;
  intro?: React.ReactNode;
  required: ConsentTopic[];
  optional: ConsentTopic[];
  optionalTitle: string;
  /** i18n keys of the terms sentence: before the link, the link, after it. */
  termsKeys: [string, string, string];
  declineLabel: string;
  onConfirm: (ticked: Set<TopicKey>) => Promise<void>;
  onDecline: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [ticked, setTicked] = useState<Set<TopicKey>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<null | "retry" | "error">(null);

  const terms = required.find((x) => x.key === "terms");
  const notice = required.find((x) => x.kind === "NOTICE");
  const requiredTicked = !terms || ticked.has("terms");
  // Why Accept selected / Continue is disabled, tied to the buttons (aria-describedby) and shown under the list.
  const hintId = `${testId}-required-hint`;
  const describedBy = requiredTicked ? undefined : hintId;

  function toggle(key: TopicKey) {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function confirm(keys: Set<TopicKey>) {
    setBusy(true);
    setFailed(null);
    try {
      await onConfirm(keys);
    } catch (e) {
      // 503 = nothing recorded (age check / legal-site commit unavailable): never treated as accepted.
      logger.warn("[Consent] gate answer failed", safeError(e));
      setFailed(isConsentTryAgain(e) ? "retry" : "error");
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    setBusy(true);
    try {
      await onDecline();
    } finally {
      setBusy(false);
    }
  }

  const all = new Set<TopicKey>([...required, ...optional].map((x) => x.key));

  return (
    <ConsentDialog testId={testId} title={title}>
      <DialogTitle>{title}</DialogTitle>
      <DialogText>{text}</DialogText>
      {intro}
      <GroupTitle>{t("consent_group_required")}</GroupTitle>
      {terms && (
        <div className="flex items-start gap-3">
          <input
            id={`${testId}-terms`}
            type="checkbox"
            data-testid="consent-item-terms"
            checked={ticked.has("terms")}
            disabled={busy}
            onChange={() => toggle("terms")}
            className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer accent-[var(--primary-fill)]"
          />
          <label htmlFor={`${testId}-terms`} className="text-sm text-[var(--foreground)]">
            <LinkedLine
              before={t(termsKeys[0])}
              link={t(termsKeys[1])}
              after={t(termsKeys[2])}
              onLink={() => openConsentPage(terms)}
              testId={`${testId}-terms-link`}
            />
          </label>
        </div>
      )}
      {notice && (
        <p className="mt-3 mb-0 text-sm text-[var(--foreground-muted)]">
          <LinkedLine
            before={t("consent_bys_notice_before")}
            link={t("consent_bys_notice_link")}
            after={t("consent_bys_notice_after")}
            onLink={() => openConsentPage(notice)}
            testId={`${testId}-notice-link`}
          />
        </p>
      )}
      {optional.length > 0 && (
        <>
          <GroupTitle>{optionalTitle}</GroupTitle>
          <ConsentChecklist topics={optional} ticked={ticked} onToggle={toggle} disabled={busy} />
        </>
      )}
      {!requiredTicked && (
        <p id={hintId} data-testid={hintId} className="mt-3 mb-0 text-xs text-[var(--foreground-muted)]">
          {t("consent_required_hint")}
        </p>
      )}
      {failed && (
        <DialogError testId={`${testId}-error`}>
          {failed === "retry" ? t("consent_try_again") : t("consent_error")}
        </DialogError>
      )}
      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={`${testId}-decline`}
          className={SECONDARY_BUTTON}
          disabled={busy}
          onClick={decline}
        >
          {declineLabel}
        </button>
        {optional.length > 0 ? (
          <>
            <button
              type="button"
              data-testid={`${testId}-accept-selected`}
              className={PRIMARY_BUTTON}
              disabled={busy || !requiredTicked}
              aria-describedby={describedBy}
              onClick={() => confirm(new Set(ticked))}
            >
              {t("consent_accept_selected")}
            </button>
            <button
              type="button"
              data-testid={`${testId}-accept-all`}
              className={PRIMARY_BUTTON}
              disabled={busy}
              onClick={() => {
                setTicked(all);
                void confirm(all);
              }}
            >
              {t("consent_accept_all")}
            </button>
          </>
        ) : (
          <button
            type="button"
            data-testid={`${testId}-continue`}
            className={PRIMARY_BUTTON}
            disabled={busy || !requiredTicked}
            aria-describedby={describedBy}
            onClick={() => confirm(new Set(ticked))}
          >
            {t("consent_continue")}
          </button>
        )}
      </div>
    </ConsentDialog>
  );
}
